import { configureQuery, parseDateValue, parseQuery } from '@trm/ui-kit-admin/query-search';
import { Schema } from 'effect';
import { matchEitherSubscriptionId, matchSimpleSearch } from '~/utils/accountFilters';
import {
  graphqlIntegerSchema,
  integerQueryOperators,
  stringQueryOperators,
} from '~/utils/queryFilterOperators';

import type { AccountFilter, StringFilter } from '../../../../../../generated/types';
import { STATUS_TYPES, accountStageOptions, accountTypeOptions } from '../constants';

const accountStringOperators = (wrap: (value: StringFilter) => AccountFilter) => ({
  ...stringQueryOperators(wrap),
  '=': (value: string) => wrap({ equalTo: value }),
  '!=': (value: string) => wrap({ notEqualTo: value }),
  IN: (values: readonly string[]) => wrap({ in: [...values] }),
  'NOT IN': (values: readonly string[]) => wrap({ notIn: [...values] }),
});

const dateRange = (value: string) => {
  const range = parseDateValue(value, new Date());
  if (range === null) throw new Error('Invalid start date');
  return { from: range.from.toISOString(), to: range.to.toISOString() };
};

const dateEquals = (value: string): AccountFilter => {
  const { from, to } = dateRange(value);
  if (from === to) return { startDate: { equalTo: from } };
  return { startDate: { greaterThanOrEqualTo: from, lessThan: to } };
};
const dateAtMost = (value: string): AccountFilter => {
  const { from, to } = dateRange(value);
  if (from === to) return { startDate: { lessThanOrEqualTo: from } };
  return { startDate: { lessThan: to } };
};
const dateAfter = (value: string): AccountFilter => {
  const { from, to } = dateRange(value);
  if (from === to) return { startDate: { greaterThan: from } };
  return { startDate: { greaterThanOrEqualTo: to } };
};

export const accountsQuery = configureQuery<AccountFilter>({
  entity: 'accounts',
  defaultSimpleField: 'name',
  searchPlaceholder: 'Search account name, owner email, owner name or subscription ID',
  and: (left, right) => ({ and: [left, right] }),
  or: (left, right) => ({ or: [left, right] }),
  not: (filter) => ({ not: filter }),
  text: (input) =>
    input.trim() === ''
      ? undefined
      : {
          or: Schema.is(graphqlIntegerSchema)(Number(input))
            ? matchEitherSubscriptionId({ equalTo: Number(input) })
            : matchSimpleSearch(input.trim()),
        },
  fields: [
    {
      name: 'id',
      label: 'ID',
      type: 'id',
      valueSchema: graphqlIntegerSchema,
      operators: integerQueryOperators((rowId) => ({ rowId })),
    },
    {
      name: 'name',
      label: 'Account Name',
      type: 'text',
      aliases: ['accountName'],
      operators: accountStringOperators((accountName) => ({ accountName })),
    },
    {
      name: 'status',
      label: 'Status',
      type: 'text',
      suggestedValues: Schema.decodeUnknownSync(Schema.NonEmptyArray(Schema.String))(STATUS_TYPES),
      operators: accountStringOperators((status) => ({ status })),
    },
    {
      name: 'stage',
      label: 'Stage',
      type: 'text',
      suggestedValues: Schema.decodeUnknownSync(Schema.NonEmptyArray(Schema.String))(
        accountStageOptions,
      ),
      operators: accountStringOperators((stage) => ({ stage })),
    },
    {
      name: 'email',
      label: 'Email',
      type: 'text',
      operators: accountStringOperators((email) => ({ user: { email } })),
    },
    {
      name: 'owner',
      label: 'Trader Name',
      type: 'text',
      operators: accountStringOperators((userFullName) => ({
        user: { userContactInfo: { userFullName } },
      })),
    },
    {
      name: 'product',
      label: 'Account Type',
      type: 'text',
      aliases: ['accountType'],
      suggestedValues: accountTypeOptions,
      operators: accountStringOperators((accountName) => ({
        accountTemplateRelation: { accountName },
      })),
    },
    {
      name: 'subscriptionId',
      label: 'Subscription ID',
      type: 'id',
      valueSchema: graphqlIntegerSchema,
      operators: {
        '=': (value) => ({ or: matchEitherSubscriptionId({ equalTo: value }) }),
        IN: (values) => ({ or: matchEitherSubscriptionId({ in: [...values] }) }),
      },
    },
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date',
      description: 'Inclusive bounds. Use an ISO timestamp with timezone for an exact time.',
      operators: {
        '>=': (value) => ({ startDate: { greaterThanOrEqualTo: dateRange(value).from } }),
        '<=': dateAtMost,
        '>': dateAfter,
        '<': (value) => ({ startDate: { lessThan: dateRange(value).from } }),
        '=': dateEquals,
      },
    },
    {
      name: 'complimentary',
      label: 'Complimentary',
      type: 'boolean',
      operators: { '=': (value) => ({ complimentary: { equalTo: value } }) },
    },
  ],
});

export const canCommitAccountsSearch = (search: string): boolean => {
  const parsed = parseQuery(search, accountsQuery.schema);
  return parsed.kind === 'text' || parsed.kind === 'valid';
};

export const withAccountIds = (
  filter: AccountFilter | undefined,
  ids: readonly number[] | null,
): AccountFilter | undefined => {
  if (ids === null) return filter;
  const idClause: AccountFilter = { rowId: { in: [...ids] } };
  return filter === undefined ? idClause : { and: [idClause, filter] };
};

export const buildAccountFilter = (search: string): AccountFilter | undefined => {
  const parsed = parseQuery(search, accountsQuery.schema);
  if (parsed.kind === 'text') return accountsQuery.text(parsed.text);
  if (parsed.kind === 'valid') return accountsQuery.compile(parsed.expression);
  return { rowId: { in: [] } };
};
